import React, { useMemo } from 'react';
import { type AITTheme, useAITTheme } from '@this/shared/ui/theme';

import type SelectStore from '@this/src/domain/select_store';
import type SelectRepository from '@this/src/domain/select_repository';
import type SearchQuery from '@this/domain/search_query';
import type SearchQueryItem from '@this/src/domain/search_query_item';
import type TransitList from '@this/domain/transit/transit_list';

import SelectListAreaSearchTemplate from '../list_area_search/list_area_search.template';
import TransportListShortDistance from '../transport_list/TransportListShortDistance';
import FlightCondition from '../../flight_condition/FlightCondition';

interface TransportListAreaProps {
  query: SearchQuery;
  store: SelectStore;
  repository: SelectRepository;
  icon: string;
  listWrapper: TransitList;
  queryItem?: SearchQueryItem;
  direction?: string;
  showSearchBox: boolean;
  toggleShowSearchBox: () => void;
  transportSegment: number | undefined;
  isOpenFlightCondition: boolean;
  errors: string[];
  handleTransportSegmentClick: (i: number) => () => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setItemLoading: (loading: boolean) => void;
  handleFlightConditionSubmit: (
    filteredUpgradeSeat: boolean,
    carrierIds: string[],
    changeablePrices: string[],
    showConnectingAir: boolean
  ) => void;
  openFlightCondition: () => void;
  closeFlightCondition: () => void;
}

const TransportListArea: React.FC<TransportListAreaProps> = ({
  query,
  store,
  repository,
  icon,
  listWrapper,
  queryItem,
  direction,
  showSearchBox,
  toggleShowSearchBox,
  handleSubmit,
  setItemLoading,
  handleFlightConditionSubmit,
  openFlightCondition,
  closeFlightCondition,
  isOpenFlightCondition,
  transportSegment,
  handleTransportSegmentClick,
  errors
}) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  if (listWrapper === null) return null;

  return (
    <>
      {store.result.type === 'separate' &&
        (store.result.isRoundTripSearchResult() || store.result.isMultipleLocationsSearchResult()) &&
        store.result.items.length > 0 &&
        query &&
        queryItem && (
          <SelectListAreaSearchTemplate
            showSearchBox={showSearchBox}
            toggleShowSearchBox={toggleShowSearchBox}
            handleSubmit={handleSubmit}
            queryItem={queryItem}
            query={query}
            errors={errors}
          />
        )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          marginBottom: '10px'
        }}
      >
        <span>
          <div style={styles.icon}>{icon}</div>
          <div style={styles.title}>
            <span>すべての候補(</span>
            <span data-wovn-ignore>{listWrapper.length()}</span>
            <span>件)</span>
          </div>
        </span>
        {listWrapper.package === false || listWrapper.package === undefined ? (
          <>
            <div className="transport-list-area__tab-buttons">
              <button
                type="button"
                className={`select-list-area__header__button transport-list-area__tab-button ${
                  transportSegment === 0 ? 'transport-list-area__tab-button--active' : ''
                }`}
                onClick={handleTransportSegmentClick(0)}
                aria-pressed={transportSegment === 0}
              >
                飛行機
              </button>
              <button
                type="button"
                className={`select-list-area__header__button transport-list-area__tab-button ${
                  transportSegment === 1 ? 'transport-list-area__tab-button--active' : ''
                }`}
                onClick={handleTransportSegmentClick(1)}
                aria-pressed={transportSegment === 1}
              >
                鉄道
              </button>
            </div>
            {transportSegment === 0 && (
              <div className="transport-list-area__flight-conditions">
                <button
                  type="button"
                  className="transport-list-area__condition-link"
                  onClick={openFlightCondition}
                >
                  <img
                    className="transport-list-area__condition-icon"
                    src="/images/filter_icon.png"
                    alt="フィルターアイコン"
                  />
                  絞り込み
                </button>
              </div>
            )}
          </>
        ) : null}

        {store.result.type === 'jrPackage' && (
          <span style={styles.small}>
            ※パッケージ商品の新幹線チケットは日時の変更が一切できません
            <br />
            通常の個別新幹線チケットとは異なる種類のチケットですのでご注意ください
          </span>
        )}
      </div>
      {(listWrapper.package === false || listWrapper.package === undefined) && (
        <FlightCondition
          queryItem={queryItem}
          listWrapper={listWrapper}
          showUpgradeSeatBox={store.result.query.firstTravelerDomesticAirSeatUpgrade()}
          open={isOpenFlightCondition}
          onSubmit={handleFlightConditionSubmit}
          onAbort={closeFlightCondition}
        />
      )}
      <div style={styles.body}>
        <TransportListShortDistance
          queryItem={queryItem}
          transits={listWrapper}
          repository={repository}
          store={store}
          onChange={store.setTab('none')}
          direction={direction}
          parentLoading={setItemLoading}
        />
      </div>
    </>
  );
};

function getStyles(theme: AITTheme) {
  return {
    icon: {
      display: 'inline-block',
      textAlign: 'center',
      width: '20px',
      height: '20px',
      background: theme.iconColor,
      borderRadius: '10px',
      color: 'white',
      fontWeight: 'bold',
      marginRight: '10px'
    } as React.CSSProperties,
    title: {
      display: 'inline-block',
      fontWeight: 'bold',
      marginRight: '10px'
    } as React.CSSProperties,
    small: {
      fontSize: '10px'
    } as React.CSSProperties,
    body: {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '66vh',
      overflowY: 'scroll',
      WebkitOverflowScrolling: 'touch'
    } as React.CSSProperties
  } as const;
}

export default TransportListArea;
